/**
 * @file: FloatingChatPanel.tsx
 *
 * The docked floating chat window — modelled on the AI assistant overlay so it
 * is NOT a modal: the container is an `absoluteFill` with `pointerEvents:
 * 'box-none'`, so on desktop the page stays fully interactive while chatting
 * (only the panel captures touches; there is no dimmer). On mobile it's a
 * bottom sheet (85% of the viewport) over a tap-to-close dimmer.
 *
 * The panel chrome (this file) owns only its appear/dismiss animation; the
 * pages inside — conversation, room detail, contact picker — are a real
 * `FloatingChatStack` (a standalone navigator reusing the inbox chat screens),
 * so page transitions / back / headers are the navigator's job, not ours.
 * Keyed by the selection `key` so switching conversations resets the stack.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { FC, memo, useCallback } from 'react';
import {
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  SlideInDown,
  SlideInRight,
  SlideOutDown,
  SlideOutRight,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject } from 'rxjs';
import { FloatingChatStack } from './FloatingChatStack';
import { FloatingChatSelection } from './context';

const DESKTOP_WIDTH = 420;
const DESKTOP_MARGIN = 16;

export const FloatingChatPanel: FC<{
  active$: BehaviorSubject<FloatingChatSelection | null>;
}> = memo(({ active$ }) => {
  const [active] = useBehaviorSubject(active$);
  const isMobile = useIsMobileLayout();
  const insets = useSafeAreaInsets();
  const { rpx } = useResponsiveContext();
  const { height: windowHeight } = useWindowDimensions();

  const close = useCallback(() => active$.next(null), [active$]);

  if (!active) return null;

  // The pages live in a standalone navigator. Re-key on the selection so
  // opening a different conversation resets the stack to its conversation root.
  const panelBody = (
    <FloatingChatStack
      key={active.key}
      intent={{ kind: 'resolved', handle: active.handle }}
      onClose={close}
    />
  );

  if (isMobile) {
    const panelHeight = windowHeight * 0.85;
    return (
      <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
        <Animated.View
          entering={FadeIn.duration(200)}
          exiting={FadeOut.duration(180)}
          style={[StyleSheet.absoluteFill, styles.dimmer]}
        >
          <TouchableOpacity
            style={StyleSheet.absoluteFill}
            activeOpacity={1}
            onPress={close}
          />
        </Animated.View>
        <Animated.View
          entering={SlideInDown.springify().damping(22).stiffness(200)}
          exiting={SlideOutDown.duration(220)}
          style={[
            styles.panel,
            styles.mobilePanel,
            {
              height: panelHeight,
              borderTopLeftRadius: rpx(20),
              borderTopRightRadius: rpx(20),
            },
          ]}
        >
          {/* No KeyboardAvoidingView: the panel sits at the screen bottom and
              WiredComposer self-manages the keyboard inset (paddingBottom),
              same as the full-screen chat. Wrapping it in a KAV double-handles
              the keyboard and collapses the message list. */}
          {panelBody}
        </Animated.View>
      </View>
    );
  }

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="box-none">
      <Animated.View
        entering={SlideInRight.springify().damping(22).stiffness(200)}
        exiting={SlideOutRight.duration(200)}
        style={[
          styles.panel,
          styles.desktopPanel,
          {
            width: DESKTOP_WIDTH,
            height: windowHeight * 0.6,
            right: DESKTOP_MARGIN,
            bottom: insets.bottom + DESKTOP_MARGIN,
          },
        ]}
      >
        {panelBody}
      </Animated.View>
    </View>
  );
});

const styles = StyleSheet.create({
  dimmer: { backgroundColor: 'rgba(0,0,0,0.35)' },
  panel: { position: 'absolute', backgroundColor: '#fff', overflow: 'hidden' },
  mobilePanel: {
    left: 0,
    right: 0,
    bottom: 0,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.12,
    shadowRadius: 20,
    elevation: 20,
  },
  desktopPanel: {
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 40,
    elevation: 32,
  },
});
